/**
 * Expand creative master stills into closest-slot confirm rows after bulk drop.
 * @module gui/components/images-upload-bulk-page/bulk-matrix-expand
 */
import { Map, List } from 'immutable'
import { map as mapPromise } from 'bluebird'
import {
  SLOT_FILL_EMPTY,
  SLOT_FILL_NONE,
  SLOT_FILL_REPLACE,
  SQUARE_SOURCE_16X9,
  buildSkipTypes,
  createAssetsRenderPreviewUrl,
  getDerivedExpandTargets,
  getExpandTargets,
  getMasterFamily,
  getPrimarySlotType,
  isMasterSlotType,
  normalizeSlotFillMode,
  normalizeSquareSource,
  squareSkipTypesForMaster,
} from './closest-slot-matrix'
import { originalFileNameForSlot } from './filename-utils'

const IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY = 'bulkUpload'

export const LIVE_LOOKUP_FAILED_MESSAGE =
  'Could not load existing catalog images for this title. Gen-fill will not publish so unknown slots are not overwritten. Choose Replace existing slot images to publish them anyway.'

/**
 * /v1/image-content-relationship-list returns `{ images: [...] }`, not a bare list.
 */
export function liveSlotTypesFromResponse (live) {
  if (!live) {
    return []
  }
  let records
  if (List.isList(live)) {
    records = live
  } else if (Array.isArray(live)) {
    records = List(live)
  } else if (typeof live.get === 'function' && live.get('images') != null) {
    const images = live.get('images')
    records = List.isList(images) ? images : List(images)
  } else if (live.images) {
    records = List(live.images)
  } else {
    return []
  }
  return records
    .map((image) => {
      if (!image || typeof image.getIn !== 'function') {
        return undefined
      }
      return image.getIn(['contentRelationship', 'type']) || image.get('type')
    })
    .filter(Boolean)
    .toArray()
}

function masterGroupKey (item) {
  const cr = item.getIn(['data', 'contentRelationship']) || Map()
  const family = getMasterFamily(cr.get('type'))
  return `${cr.get('d6Id')}|${cr.get('language')}|${family}`
}

function pixelArea (item) {
  return Number(item.get('sourceWidth') || 0) * Number(item.get('sourceHeight') || 0)
}

function seedValue (seeds, type) {
  if (!seeds) {
    return 1
  }
  if (typeof seeds.get === 'function') {
    return seeds.get(type) || 1
  }
  return seeds[type] || 1
}

function contentIdentityKey (item) {
  const cr = item.getIn(['data', 'contentRelationship']) || Map()
  return `${cr.get('d6Id')}|${cr.get('d6ContentType')}|${cr.get('language')}`
}

function slotTypeOf (item) {
  return item.getIn(['data', 'contentRelationship', 'type'])
}

function explicitNonMasterTypes (bulk, sourceItem) {
  const sourceKey = contentIdentityKey(sourceItem)
  return bulk
    .valueSeq()
    .filter((item) => !item.get('isMatrixDerived'))
    .filter((item) => contentIdentityKey(item) === sourceKey)
    .map((item) => slotTypeOf(item))
    .filter((type) => type && !isMasterSlotType(type))
    .toSet()
    .toArray()
}

/**
 * Other uploaded masters in this family (regular 16:9 beside 4K, or the clean pair).
 * They publish on their own slot and must not be replaced by a derived fill.
 */
function explicitSiblingMasterTypes (bulk, sourceItem) {
  const sourceKey = contentIdentityKey(sourceItem)
  const family = getMasterFamily(slotTypeOf(sourceItem))
  const ownType = slotTypeOf(sourceItem)
  if (!family) {
    return []
  }
  return bulk
    .valueSeq()
    .filter((item) => !item.get('isMatrixDerived') && item.get('isMatrixSource'))
    .filter((item) => contentIdentityKey(item) === sourceKey)
    .map((item) => slotTypeOf(item))
    .filter((type) => type && type !== ownType && getMasterFamily(type) === family)
    .toSet()
    .toArray()
}

/**
 * Every other family target, so an explicit master attaches only its own slot.
 * Uses the full 16:9 square set so a later square-source change cannot start fills.
 */
function explicitSlotSkipTypes (item) {
  const ownType = slotTypeOf(item)
  return getExpandTargets(ownType, {
    contentType: item.get('contentType'),
    includePrimary: true,
    squareSource: SQUARE_SOURCE_16X9,
  })
    .map((target) => target.type)
    .filter((type) => type !== ownType)
}

/**
 * Unattached matrix source ids still sitting in the bulk session (not yet placed).
 */
export function unpublishedMatrixSourceIds (application) {
  return application
    .get(IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, Map())
    .filter((item) => item.get('isMatrixSource') && item.get('sourceImageId'))
    .valueSeq()
    .map((item) => item.get('sourceImageId'))
    .toSet()
    .toArray()
}

/**
 * True while files are reading or closest-slot expand is still running.
 */
export function isBulkSessionBusy (images, application) {
  if (application.get('matrixExpanding') === true) {
    return true
  }
  return !images.filter((v) => v.get('compressing')).isEmpty()
}

/**
 * Gen-fill and closest-slot rows wait on the assets-render preview.
 * Direct uploads (hero, logo, and other non-matrix files) do not.
 */
export function rowUsesPreviewGate (item) {
  if (!item || item.get('omitPrimary')) {
    return false
  }
  return Boolean(item.get('genFill') || item.get('isMatrixDerived') || item.get('isMatrixSource'))
}

/**
 * True until every gated row with a proposed preview URL has reported a successful load.
 * Failed previews stay blocked; gen-fill Regenerate (or Reset) is the recovery path.
 */
export function isBulkPublishBlockedByPreviews (images, previewStatusById) {
  const statuses = previewStatusById || Map()
  return images.some((item, itemId) => {
    if (!rowUsesPreviewGate(item) || !item.getIn(['data', 'url'])) {
      return false
    }
    return statuses.get(itemId) !== 'ready'
  })
}

export function bulkPublishPreviewWaitMessage (images, previewStatusById) {
  const statuses = previewStatusById || Map()
  const failed = images.some((item, itemId) => {
    if (!rowUsesPreviewGate(item) || !item.getIn(['data', 'url'])) {
      return false
    }
    return statuses.get(itemId) === 'failed'
  })
  if (failed) {
    return 'Some previews failed. Use Regenerate on gen-fill rows, or Reset Images and drop again.'
  }
  return 'Waiting for previews to finish. Resize slots publish from Imgix if cache is still writing; gen-fill still needs the preview cache.'
}

/**
 * Rebuild derived confirm rows from stored sources. Live slots stay in skipTypes for
 * persist; confirm rows still render so publishers can compare proposed vs live.
 * Mode `none` publishes only the uploaded file and does not add those rows.
 */
export function syncMatrixDerivedConfirmRows (applicationData, options = {}) {
  const { slotFillMode = SLOT_FILL_EMPTY, server = '' } = options
  const mode = normalizeSlotFillMode(slotFillMode)
  const squareSource = normalizeSquareSource(
    options.squareSource != null ? options.squareSource : applicationData.get('squareSource'),
    { omittedMeans16x9: false },
  )
  let bulk = applicationData
    .get(IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, Map())
    .filter((item) => !item.get('isMatrixDerived'))

  bulk.forEach((item, itemId) => {
    if (!item.get('isMatrixSource')) {
      return
    }
    if (item.get('explicitSlotOnly')) {
      const ownType = slotTypeOf(item)
      const uuid = item.get('sourceFileSystemKey')
      const sourcePreviewUrl = createAssetsRenderPreviewUrl(server, uuid)
      bulk = bulk.mergeIn(
        [itemId],
        Map({
          primaryType: ownType,
          skipTypes: explicitSlotSkipTypes(item),
          explicitSlotOnly: true,
          isFamilySource: false,
          ...(sourcePreviewUrl ? { data: item.get('data').set('url', sourcePreviewUrl) } : {}),
        }),
      )
      if (item.get('error') === LIVE_LOOKUP_FAILED_MESSAGE) {
        bulk = bulk.deleteIn([itemId, 'error'])
      }
      return
    }
    const explicitTypes = [
      ...explicitNonMasterTypes(bulk, item),
      ...explicitSiblingMasterTypes(bulk, item),
    ]
    const primaryType = item.get('primaryType')
    const contentType = item.get('contentType')
    const liveTypes = item.get('liveTypes')
    let liveTypesArray = List.isList(liveTypes) ? liveTypes.toArray() : liveTypes || []
    const removedTypes = item.get('removedTypes')
    const removedArray = List.isList(removedTypes) ? removedTypes.toArray() : removedTypes || []
    const squareSkips = squareSkipTypesForMaster(primaryType, squareSource)
    const expandSkipTypes = [...explicitTypes, ...removedArray, ...squareSkips]
    if (item.get('liveLookupFailed') && mode !== SLOT_FILL_REPLACE) {
      liveTypesArray = getDerivedExpandTargets(primaryType, { contentType, squareSource })
        .filter((target) => target.genFill)
        .map((target) => target.type)
    }
    const derivedTargets = getDerivedExpandTargets(primaryType, {
      skipTypes: expandSkipTypes,
      contentType,
      squareSource,
    })
    const persistSkipTypes = [
      ...new Set([
        ...buildSkipTypes({
          liveTypes: liveTypesArray,
          explicitTypes,
          derivedTypes: derivedTargets.map((target) => target.type),
          slotFillMode: mode,
        }),
        ...removedArray,
        ...squareSkips,
        ...(item.get('omitPrimary') && primaryType ? [primaryType] : []),
      ]),
    ]
    const uuid = item.get('sourceFileSystemKey')
    const sourceSize = {
      sourceWidth: item.get('sourceWidth'),
      sourceHeight: item.get('sourceHeight'),
    }
    const cr = item.getIn(['data', 'contentRelationship']) || Map()
    const previousSeeds = item.get('seeds')
    const seeds = {}
    if (mode !== SLOT_FILL_NONE) {
      derivedTargets.forEach((target) => {
        const seed = seedValue(previousSeeds, target.type)
        if (target.genFill) {
          seeds[target.type] = seed
        }
        const derivedId = `${cr.get('d6Id')}|${cr.get('d6ContentType')}|${target.type}`
        if (derivedId === itemId) {
          return
        }
        bulk = bulk.set(
          derivedId,
          Map({
            id: derivedId,
            isMatrixDerived: true,
            parentItemId: itemId,
            genFill: Boolean(target.genFill),
            seed,
            data: Map({
              originalFileName: originalFileNameForSlot(
                item.getIn(['data', 'originalFileName']),
                target.type,
              ),
              url: createAssetsRenderPreviewUrl(server, uuid, target, seed, sourceSize),
              contentRelationship: cr.set('type', target.type),
            }),
          }),
        )
      })
    }
    const sourcePreviewUrl = createAssetsRenderPreviewUrl(server, uuid)
    bulk = bulk.mergeIn(
      [itemId],
      Map({
        skipTypes: persistSkipTypes,
        seeds,
        ...(sourcePreviewUrl ? { data: item.get('data').set('url', sourcePreviewUrl) } : {}),
      }),
    )
    if (
      item.get('error') === LIVE_LOOKUP_FAILED_MESSAGE &&
      (!item.get('liveLookupFailed') || mode === SLOT_FILL_REPLACE || mode === SLOT_FILL_NONE)
    ) {
      bulk = bulk.deleteIn([itemId, 'error'])
    }
  })

  return applicationData.set(IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, bulk)
}

function listFrom (value) {
  if (List.isList(value)) {
    return value
  }
  if (!value) {
    return List()
  }
  return List(value)
}

function rememberRemovedType (bulk, parentId, type) {
  if (!parentId || !type || !bulk.get(parentId)) {
    return bulk
  }
  const withRemoved = bulk.updateIn([parentId, 'removedTypes'], List(), (current) => {
    const types = listFrom(current)
    return types.includes(type) ? types : types.push(type)
  })
  return withRemoved.updateIn([parentId, 'skipTypes'], List(), (current) => {
    const types = listFrom(current)
    return types.includes(type) ? types : types.push(type)
  })
}

function dropSessionSource (applicationData, sourceImageId) {
  if (!sourceImageId) {
    return applicationData
  }
  const existing = applicationData.get('sessionSourceImageIds')
  const ids = List.isList(existing) ? existing.toArray() : existing || []
  return applicationData.set(
    'sessionSourceImageIds',
    ids.filter((id) => id !== sourceImageId),
  )
}

/**
 * Remove one confirm row. A master with remaining derived rows stays as the
 * unpublished source and skips only its own slot. The unattached source image
 * is deleted only when nothing in the session still uses it.
 */
export function removeBulkConfirmRow (applicationData, itemId) {
  const bulk = applicationData.get(IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, Map())
  const item = bulk.get(itemId)
  if (!item) {
    return { applicationData, sourceImageIdToDelete: undefined }
  }
  if (item.get('isMatrixDerived')) {
    const parentId = item.get('parentItemId')
    const type = item.getIn(['data', 'contentRelationship', 'type'])
    let nextBulk = rememberRemovedType(bulk.delete(itemId), parentId, type)
    const parent = nextBulk.get(parentId)
    const siblings = nextBulk.filter((row) => row.get('parentItemId') === parentId)
    if (parent && parent.get('omitPrimary') && siblings.isEmpty()) {
      const sourceImageId = parent.get('sourceImageId')
      nextBulk = nextBulk.delete(parentId)
      return {
        applicationData: dropSessionSource(
          applicationData.set(IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, nextBulk),
          sourceImageId,
        ),
        sourceImageIdToDelete: sourceImageId,
      }
    }
    return {
      applicationData: applicationData.set(IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, nextBulk),
      sourceImageIdToDelete: undefined,
    }
  }
  if (item.get('isMatrixSource')) {
    const children = bulk.filter((row) => row.get('parentItemId') === itemId)
    if (!children.isEmpty()) {
      const primaryType =
        item.get('primaryType') || item.getIn(['data', 'contentRelationship', 'type'])
      let nextBulk = bulk.setIn([itemId, 'omitPrimary'], true)
      nextBulk = rememberRemovedType(nextBulk, itemId, primaryType)
      return {
        applicationData: applicationData.set(IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, nextBulk),
        sourceImageIdToDelete: undefined,
      }
    }
    const sourceImageId = item.get('sourceImageId')
    return {
      applicationData: dropSessionSource(
        applicationData.set(IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, bulk.delete(itemId)),
        sourceImageId,
      ),
      sourceImageIdToDelete: sourceImageId,
    }
  }
  return {
    applicationData: applicationData.set(
      IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY,
      bulk.delete(itemId),
    ),
    sourceImageIdToDelete: undefined,
  }
}

/**
 * POST unattached sources for new matrix masters, then add derived confirm rows.
 * A regular 16:9 and a 4K 16:9 in the same family both stay. The larger file is
 * the only expand source; the other file publishes on its own slot.
 */
export async function expandDroppedMatrixMasters (options) {
  const {
    applicationData,
    auth,
    server,
    createImage,
    getLiveImages,
    slotFillMode = SLOT_FILL_EMPTY,
    squareSource,
    concurrency = 4,
  } = options
  let nextApplication = applicationData
  const bulk = nextApplication.get(IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, Map())
  const masterEntries = bulk
    .entrySeq()
    .filter(
      ([, item]) =>
        isMasterSlotType(item.getIn(['data', 'contentRelationship', 'type'])) &&
        !item.get('isMatrixDerived') &&
        !item.get('isMatrixSource') &&
        item.getIn(['data', 'url']) &&
        !item.get('error'),
    )
    .toArray()

  await mapPromise(
    masterEntries,
    async ([itemId, item]) => {
      try {
        const uploaded = await createImage({
          auth,
          server,
          processImage: false,
          image: item.get('data').delete('contentRelationship'),
        })
        nextApplication = nextApplication.mergeIn(
          [IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, itemId],
          Map({
            sourceImageId: uploaded.get('id'),
            sourceFileSystemKey: uploaded.get('fileSystemKey'),
            sourceWidth: uploaded.get('width'),
            sourceHeight: uploaded.get('height'),
            isMatrixSource: true,
            data: item
              .get('data')
              .set('url', createAssetsRenderPreviewUrl(server, uploaded.get('fileSystemKey'))),
          }),
        )
      } catch (err) {
        nextApplication = nextApplication.setIn(
          [IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, itemId, 'error'],
          err instanceof Error ? err.message : 'Unknown error',
        )
      }
    },
    { concurrency },
  )

  const grouped = {}
  nextApplication.get(IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, Map()).forEach((item, itemId) => {
    if (!item.get('isMatrixSource')) {
      return
    }
    const key = masterGroupKey(item)
    if (!grouped[key]) {
      grouped[key] = []
    }
    grouped[key].push({
      itemId,
      item: nextApplication.getIn([IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, itemId]),
    })
  })

  const sessionSourceImageIds = []

  await mapPromise(Object.values(grouped), async (group) => {
    const winner = group.reduce((best, current) =>
      pixelArea(current.item) >= pixelArea(best.item) ? current : best,
    )
    const hasSiblingMaster = group.length > 1
    const winnerItem = nextApplication.getIn([
      IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY,
      winner.itemId,
    ])
    const cr = winnerItem.getIn(['data', 'contentRelationship']) || Map()
    const family = getMasterFamily(cr.get('type'))
    let liveLookupFailed = winnerItem.get('liveLookupFailed') === true
    let liveTypes = winnerItem.get('liveTypes')
    if (liveTypes === undefined || liveTypes === null) {
      try {
        const live = await getLiveImages({
          d6Id: cr.get('d6Id'),
          d6ContentType: cr.get('d6ContentType'),
          language: cr.get('language'),
        })
        liveTypes = liveSlotTypesFromResponse(live)
        liveLookupFailed = false
      } catch (_e) {
        liveLookupFailed = true
        liveTypes = []
      }
    }
    const familyContentType = winnerItem.get('contentType')
    group.forEach((entry) => {
      const entryItem = nextApplication.getIn([
        IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY,
        entry.itemId,
      ])
      const entryType = entryItem.getIn(['data', 'contentRelationship', 'type'])
      const isWinner = entry.itemId === winner.itemId
      sessionSourceImageIds.push(entryItem.get('sourceImageId'))
      const liveLookupPatch = {
        isMatrixSource: true,
        isFamilySource: isWinner,
        explicitSlotOnly: hasSiblingMaster && !isWinner,
        primaryType: hasSiblingMaster || !isWinner ? entryType : getPrimarySlotType(family),
        liveTypes,
        liveLookupFailed,
        contentType: entryItem.get('contentType') || familyContentType,
        sessionSource: true,
      }
      if (isWinner && liveLookupFailed) {
        liveLookupPatch.error = LIVE_LOOKUP_FAILED_MESSAGE
      }
      nextApplication = nextApplication.mergeIn(
        [IMAGE_BULK_UPLOAD_DATA_APPLICATION_KEY, entry.itemId],
        Map(liveLookupPatch),
      )
    })
  })

  const existingSessionIds = nextApplication.get('sessionSourceImageIds')
  const mergedSessionIds = [
    ...(List.isList(existingSessionIds) ? existingSessionIds.toArray() : existingSessionIds || []),
    ...sessionSourceImageIds,
  ].filter(Boolean)

  return syncMatrixDerivedConfirmRows(
    nextApplication.set('sessionSourceImageIds', [...new Set(mergedSessionIds)]),
    {
      slotFillMode,
      server,
      squareSource: squareSource != null ? squareSource : nextApplication.get('squareSource'),
    },
  )
}
